import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    getIOSVersion,
    isChromeIOS,
    isFirefoxIOS,
    isSamsungBrowser,
    isFirefoxAndroid,
    isInAppBrowser,
    isIPad,
    isIPhone,
    isAndroid,
    isMobileDevice,
    isInStandaloneMode,
    isLandscape,
    endSide,
} from '../utils/deviceDetect';

// ─── Scenario resolution ────────────────────────────────────────────────────
// Resolves to a { position, message } pair. `position` is one of:
// 'bottom-center' | 'bottom-end' | 'top-end' | 'center' (no arrow, safe fallback)
const resolveScenario = () => {
    if (isInAppBrowser()) {
        return {
            position: 'center',
            message: "This in-app browser can't install the game. Open this page in Safari or Chrome instead, then try again.",
        };
    }

    if (isIPhone()) {
        if (isLandscape()) {
            return {
                position: 'center',
                message: "Rotate back to portrait — Safari hides its toolbar in landscape. Then tap the Share icon at the bottom → 'Add to Home Screen'.",
            };
        }
        if (isChromeIOS()) {
            // iOS forces every browser through the same native Share Sheet for
            // "Add to Home Screen" — Chrome can't put it in its own ⋮ menu,
            // only offer a route into that same Share Sheet.
            return { position: 'bottom-center', message: "Tap the Share icon (square with an arrow ↑) — or tap ⋯ and choose 'Share' first. Then 'Add to Home Screen'." };
        }
        if (isFirefoxIOS()) {
            return { position: 'bottom-center', message: "Tap the Share icon in Firefox's toolbar → 'Add to Home Screen'" };
        }
        // Safari — always lives at the bottom of the screen, but iOS 15+
        // replaced the old fixed 5-icon toolbar (Share always in the same
        // spot) with a compact address bar where Share is hidden behind an
        // extra tap — and users can toggle between the two layouts in
        // Settings, so we can't tell which one is showing. Default to
        // assuming modern (iOS 15+) since pre-15 devices are now rare.
        const iosVersion = getIOSVersion();
        const classicLayout = iosVersion !== null && iosVersion < 15;
        return {
            position: 'bottom-center',
            message: classicLayout
                ? "Tap the Share icon ↓ at the bottom → 'Add to Home Screen' → confirm 'Add'"
                : "Look at the bottom of the screen. See 5 icons in a row? Tap the Share square (↑). See a compact address bar instead? Tap the small icon at its edge first, then 'Share' → 'Add to Home Screen'.",
        };
    }

    if (isIPad()) {
        if (isChromeIOS()) {
            return { position: 'top-end', message: "Tap the Share icon (square with an arrow ↑) — or tap ⋯ and choose 'Share' first. Then 'Add to Home Screen'." };
        }
        return { position: 'top-end', message: "Tap the Share icon (top corner) → 'Add to Home Screen' → confirm 'Add'" };
    }

    if (isAndroid()) {
        if (isSamsungBrowser()) {
            return { position: 'top-end', message: "Tap ⋮ (top corner) → 'Add page to' → 'Home screen'" };
        }
        if (isFirefoxAndroid()) {
            return { position: 'bottom-end', message: "Tap ⋮ (bottom corner) → 'Install' or 'Add to Home Screen'" };
        }
        // Chrome, Edge, and most other Chromium-based Android browsers.
        // Some versions also show an install icon directly in the address bar.
        return { position: 'top-end', message: "Tap ⋮ (top corner) → 'Install app' or 'Add to Home Screen' — or look for an install icon in the address bar" };
    }

    return { position: 'center', message: "Open in your mobile browser and use its menu to 'Add to Home Screen'" };
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getArrowWrapperStyle = (position) => {
    const side = endSide();
    const base = { position: 'fixed', zIndex: 9999 };
    if (position === 'bottom-center') return { ...base, bottom: 76, left: 'calc(50% - 10px)' };
    if (position === 'bottom-end')    return { ...base, bottom: 76, [side]: 20 };
    if (position === 'top-end')       return { ...base, top: 10, [side]: 20 };
    return base; // 'center' — no arrow rendered for this case
};

const getArrowStyle = (position) => {
    const base = { filter: 'drop-shadow(0 0 6px rgba(255, 191, 229, 0.6))' };
    const isBottom = position === 'bottom-center' || position === 'bottom-end';
    return isBottom
        ? { ...base, width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '16px solid #ffbfe5', animation: 'bounceDown 1.5s infinite' }
        : { ...base, width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderBottom: '16px solid #ffbfe5', animation: 'bounceUp 1.5s infinite' };
};

const getBoxStyle = (position) => {
    const side = endSide();
    const base = {
        position: 'fixed',
        zIndex: 9998,
        backgroundColor: 'rgba(20, 10, 30, 0.95)',
        color: '#ffdaf4',
        padding: '14px 18px',
        borderRadius: '14px',
        maxWidth: '300px',
        fontFamily: 'Playfair Display, serif',
        fontSize: '0.95rem',
        lineHeight: 1.4,
        boxShadow: '0 0 8px rgba(255, 191, 229, 0.25)',
    };
    if (position === 'bottom-center') return { ...base, bottom: 120, left: '50%', transform: 'translateX(-50%)' };
    if (position === 'bottom-end')    return { ...base, bottom: 120, [side]: 20 };
    if (position === 'top-end')       return { ...base, top: 50, [side]: 20 };
    // 'center' — safe fallback when we can't confidently point at anything
    return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '300px', textAlign: 'center' };
};

// ─── Component ────────────────────────────────────────────────────────────────

const InstallGuide = ({ triggerRefresh = 0 }) => {
    const [showGuide, setShowGuide] = useState(false);
    const [scenario, setScenario] = useState(() => resolveScenario());

    useEffect(() => {
        const hasSeenGuide = localStorage.getItem('installGuideShown');
        if (isMobileDevice() && !hasSeenGuide && !isInStandaloneMode()) {
            setShowGuide(true);
            localStorage.setItem('installGuideShown', 'true');
        }
        setScenario(resolveScenario());
    }, [triggerRefresh]);

    useEffect(() => {
        const recompute = () => setScenario(resolveScenario());
        window.addEventListener('resize', recompute);
        window.addEventListener('orientationchange', recompute);
        return () => {
            window.removeEventListener('resize', recompute);
            window.removeEventListener('orientationchange', recompute);
        };
    }, []);

    if (!showGuide) return null;

    const { position, message } = scenario;

    return (
        <>
            {position !== 'center' && (
                <Box sx={getArrowWrapperStyle(position)}>
                    <Box sx={getArrowStyle(position)} />
                </Box>
            )}
            <Box sx={getBoxStyle(position)}>
                <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
                    <IconButton
                        onClick={() => {
                            localStorage.setItem('installGuideShown', 'true');
                            setShowGuide(false);
                        }}
                        size="small"
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body1">
                    {message}
                </Typography>
            </Box>
            <style>{`
                @keyframes bounceDown {
                    0%, 100% { transform: translateY(0);   }
                    50%       { transform: translateY(8px); }
                }
                @keyframes bounceUp {
                    0%, 100% { transform: translateY(0);    }
                    50%       { transform: translateY(-8px); }
                }
            `}</style>
        </>
    );
};

export default InstallGuide;
